import React, { useEffect } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgCarPlate from '@components/ringil3/icons/CarPlate.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import RingilTextArea from '@components/framework/input/RingilTextArea';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import * as yup from 'yup';
import { findErrorMessage } from '@utils/validationUtils.ts';

interface ComponentProps {
  carPlate: string;
  onChange: (carPlate: string) => void;
  readOnly?: boolean;
  required?: boolean;
  maxLength?: number;
  dataTestId: string;
  secured?: SecuredConfig;
}

interface Form {
  carPlate: string;
}

const CarPlateTag: React.FC<ComponentProps> = ({ carPlate, onChange, readOnly, required = false, maxLength, dataTestId, secured }) => {
  const { tr } = useTranslationLgs();
  const requiredMessage = tr('validation.required', 'Value is required');
  const displayValue = (carPlate ?? '').trim() === '' ? undefined : carPlate;

  const form = useForm<Form>(
    yup.object().shape({
      carPlate: required ? yup.string().nullable().required(requiredMessage).matches(/\S/, requiredMessage) : yup.string().nullable(),
    }),
    { carPlate },
    (values, onSuccess) => save(values, onSuccess),
    false,
    false,
    null,
    'none',
  );

  const carPlatePane = () => {
    return (
      <VerticalFieldAndValue
        label={tr(`CarPlateTag.title`, `Car plate`)}
        labelMode={'normal'}
        required={required}
        errorMessage={findErrorMessage(form.validationErrors, 'carPlate', tr)}
      >
        <RingilTextArea
          rows={1}
          value={form.values.carPlate}
          onChange={v => form.setFieldValue(form.names.carPlate, v)}
          maxLength={maxLength}
          className={'resize-none'}
          dataTestId={dataTestId}
        />
      </VerticalFieldAndValue>
    );
  };

  // The tag stays mounted while ReservationDetail reloads, so re-seed the editor when the prop
  // changes; otherwise reopening the pane would write a mount-time value back over a newer one.
  useEffect(() => {
    cancel();
  }, [carPlate]);

  const cancel = () => {
    form.clear();
    form.overwriteValues({ carPlate });
  };

  const save = (values: Form, onSuccess?: () => void) => {
    if (onSuccess) onSuccess();
    onChange(values.carPlate);
  };

  return (
    <ButtonTag
      label={tr(`CarPlateTag.title`, `Car plate`)}
      value={displayValue}
      icon={<SvgCarPlate />}
      actionPane={carPlatePane()}
      actionIcon={<SvgTagEdit />}
      onSave={onSuccess => form.validateAndSendWithSuccess(onSuccess)}
      autoClose
      readonly={readOnly}
      required={required}
      onClose={() => cancel()}
      dataTestId={dataTestId}
      secured={secured}
    />
  );
};

export default CarPlateTag;
